import { Dispatch, FC, FormEvent, Fragment } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { Link, MessageBar, MessageBarType, Stack, Text } from "@fluentui/react";
import { TextField } from "cayo.ui";
import { wizardStyles } from "cayo.ui.wizards";
import styled from "styled-components";
import { commonMessages } from "@cayo/shared/messages/common";
import { regexs } from "../../../../utils/regexs";
import { messages } from "../../messages";
import { IUpdateWizardAction } from "../../reducer";
import { INewManagedTenantState } from "../../types/INewManagedTenantState";

export interface IInputValidationState {
  errors?: { [key: string]: string };
}

const handleInputChange = (
  control: { id: string; pattern?: string },
  wizardState: IInputValidationState,
  intl: IntlShape,
  newValue?: string
) => {
  const newState: IInputValidationState & Record<string, any> = { ...wizardState };
  const controlId = control.id;

  newState[controlId] = newValue;
  let pattern;

  if (!newValue) {
    if (!newState.errors) {
      newState.errors = {};
    }
    newState.errors[controlId] = intl.formatMessage(commonMessages.inputRequired);
  } else if ((pattern = control.pattern) && !newValue.match(pattern)) {
    if (!newState.errors) {
      newState.errors = {};
    }
    newState.errors[controlId] = intl.formatMessage(commonMessages.matchFormat);
  } else if (newState.errors) {
    delete newState.errors[controlId];
  }

  const newWizardState = { ...wizardState, ...newState };

  return newWizardState;
};

export const wizardTools = {
  handleInputChange,
};

const accountId = "account";
const passwordId = "password";

interface IGeneralPage {
  dispatchWizardState: Dispatch<IUpdateWizardAction>;
  wizardState: INewManagedTenantState;
}

const GeneralPage: FC<IGeneralPage> = ({ dispatchWizardState, wizardState }) => {
  const intl = useIntl();

  const { account, password, tenant, connectionIsRecommended, tenantError, errors } = wizardState;

  const handleCredentialChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    const newInputState = wizardTools.handleInputChange(
      event.target as any,
      wizardState,
      intl,
      newValue
    );

    dispatchWizardState({ action: "setCredential", newState: newInputState });
  };

  return (
    <Stack
      horizontal={false}
      tokens={wizardStyles.stackTokens}
      styles={{ root: { maxWidth: wizardStyles.maxPageWidth } }}
    >
      {tenantError && (
        <MessageBar messageBarType={MessageBarType.error} truncated={false} isMultiline>
          {tenantError.split("\r\n").map((e, i) => (
            <Fragment key={i}>
              {e}
              <br />
            </Fragment>
          ))}
        </MessageBar>
      )}

      <Text>
        {connectionIsRecommended
          ? intl.formatMessage(messages.accountRecommendation)
          : intl.formatMessage(messages.accountRequirementsLabel)}
      </Text>
      <ul>
        {!connectionIsRecommended && (
          <>
            <li>
              <Text>{intl.formatMessage(messages.accountRecommendation1)}</Text>
            </li>
            <li>
              <Text>{intl.formatMessage(messages.accountRecommendation2)}</Text>
            </li>
            <li>
              <Text>{intl.formatMessage(messages.accountRecommendation3)}</Text>
            </li>
          </>
        )}
        {connectionIsRecommended && (
          <li>
            <Text>{intl.formatMessage(messages.accountRecommendation4)}</Text>
          </li>
        )}
      </ul>
      <TextWrap>
        <TextField
          id={accountId}
          disabled={!!tenant}
          label={
            connectionIsRecommended
              ? intl.formatMessage(messages.account)
              : intl.formatMessage(messages.legacyAccount)
          }
          placeHolder={intl.formatMessage(messages.accountPlaceholder)}
          tooltip={
            connectionIsRecommended
              ? intl.formatMessage(messages.accountRequirementsPrincipal)
              : intl.formatMessage(messages.accountRequirements)
          }
          required
          type="text"
          pattern={regexs.emailRegex.source}
          value={account}
          setValue={(v, e) => handleCredentialChange(e!, v)}
          errorMessage={(() => {
            return errors && errors[accountId];
          })()}
        />
      </TextWrap>

      <Link
        onClick={() => {
          const usePasswordFlow = !wizardState.usePasswordFlow;

          const newInputState = wizardTools.handleInputChange(
            { id: passwordId },
            wizardState,
            intl,
            undefined
          );

          dispatchWizardState({
            action: "setCredential",
            newState: newInputState,
          });

          dispatchWizardState({
            action: "usePasswordFlow",
            newState: { usePasswordFlow },
          });
        }}
      >
        {wizardState.usePasswordFlow ? (
          <FormattedMessage
            id="new-tenant-wizard.general.use-modern-auth"
            defaultMessage="Use modern authentication <<"
          />
        ) : (
          <FormattedMessage
            id="new-tenant-wizard.general.use-legacy-auth"
            defaultMessage="Use legacy authentication (password flow) >>"
          />
        )}
      </Link>
      {wizardState.usePasswordFlow && (
        <>
          <Text>
            <FormattedMessage
              id="new-tenant-wizard.general.password-flow-eplanation"
              // tslint:disable-next-line: max-line-length
              defaultMessage="You can cofigure Cayosoft Guardian to use account name and password to connect to your tenant. For this password flow to work, the connection account must be excluded from all Multi-Factor Authentication (MFA) requirements."
            />
          </Text>
          <TextField
            id={passwordId}
            label={intl.formatMessage(messages.password)}
            type="password"
            required
            disabled={!!tenant}
            value={password}
            autocomplete="new-password"
            setValue={(v, e) => handleCredentialChange(e!, v)}
            errorMessage={errors && errors[passwordId]}
            dataTestId="password"
          />
        </>
      )}
    </Stack>
  );
};

const TextWrap = styled.div`
  label {
    &::after {
      padding-right: 0 !important;
    }
  }
`;

export default GeneralPage;
